<template>
  <Teleport to="body">
    <div class="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-start justify-center pt-20 z-50 backdrop-blur-sm">
      <div
        ref="paletteRef"
        class="w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden animate-slide-up"
      >
        <!-- Search input -->
        <div class="p-4 border-b border-gray-100 dark:border-gray-700">
          <div class="relative">
            <MagnifyingGlassIcon class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
            <input
              v-model="query"
              ref="searchInput"
              type="text"
              placeholder="Search commands, pages, or actions..."
              class="w-full pl-10 pr-4 py-3 text-sm border-0 focus:outline-none focus:ring-0"
              @keydown="handleKeydown"
            />
          </div>
        </div>
        
        <!-- Results -->
        <div class="max-h-96 overflow-y-auto scrollbar-custom">
          <!-- No results -->
          <div v-if="filteredCommands.length === 0 && query" class="p-8 text-center">
            <CommandLineIcon class="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p class="text-gray-500 dark:text-gray-400">No commands found</p>
          </div>
          
          <!-- Command groups -->
          <div v-else class="py-2">
            <div v-for="group in groupedCommands" :key="group.name" class="mb-4 last:mb-0">
              <div v-if="group.commands.length > 0">
                <h3 class="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {{ group.name }}
                </h3>
                <div class="space-y-1">
                  <button
                    v-for="(command, index) in group.commands"
                    :key="command.id"
                    @click="executeCommand(command)"
                    class="w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    :class="{ 'bg-primary-50 dark:bg-primary-900/30 border-r-2 border-primary-500': isSelected(command, index) }"
                  >
                    <component :is="command.icon" class="w-5 h-5 text-gray-400 dark:text-gray-500" />
                    <div class="flex-1">
                      <div class="text-sm font-medium text-gray-900 dark:text-white">
                        {{ command.title }}
                      </div>
                      <div v-if="command.description" class="text-xs text-gray-500 dark:text-gray-400">
                        {{ command.description }}
                      </div>
                    </div>
                    <div v-if="command.shortcut" class="text-xs text-gray-400 font-mono">
                      {{ command.shortcut }}
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Footer -->
        <div class="px-4 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
          <div class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <div class="flex items-center space-x-4">
              <span class="flex items-center space-x-1">
                <kbd class="px-1.5 py-0.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded text-xs text-gray-900 dark:text-gray-300">↑↓</kbd>
                <span>navigate</span>
              </span>
              <span class="flex items-center space-x-1">
                <kbd class="px-1.5 py-0.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded text-xs text-gray-900 dark:text-gray-300">⏎</kbd>
                <span>select</span>
              </span>
            </div>
            <span class="flex items-center space-x-1">
              <kbd class="px-1.5 py-0.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded text-xs text-gray-900 dark:text-gray-300">esc</kbd>
              <span>close</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

import {
  MagnifyingGlassIcon,
  CommandLineIcon,
  HomeIcon,
  MegaphoneIcon,
  UsersIcon,
  DocumentDuplicateIcon,
  BoltIcon,
  CogIcon,
  UserIcon,
  PlusIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/vue/24/outline'

defineEmits(['close'])

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

const query = ref('')
const selectedIndex = ref(0)
const paletteRef = ref(null)
const searchInput = ref(null)

// Helper to navigate with locale
const navigateTo = (routeName, params = {}) => {
  const locale = route.params.locale || 'en'
  router.push({ name: routeName, params: { ...params, locale } })
}

// Available commands
const commands = [
  // Navigation
  {
    id: 'nav-dashboard',
    title: 'Dashboard',
    description: 'Go to dashboard',
    icon: HomeIcon,
    group: 'Navigation',
    action: () => navigateTo('dashboard'),
    keywords: ['dashboard', 'home', 'overview']
  },
  {
    id: 'nav-campaigns',
    title: 'Campaigns',
    description: 'View email campaigns',
    icon: MegaphoneIcon,
    group: 'Navigation',
    action: () => navigateTo('campaigns'),
    keywords: ['campaigns', 'emails', 'marketing']
  },
  {
    id: 'nav-contacts',
    title: 'Contacts',
    description: 'Manage contacts',
    icon: UsersIcon,
    group: 'Navigation',
    action: () => navigateTo('contacts'),
    keywords: ['contacts', 'subscribers', 'lists']
  },
  {
    id: 'nav-templates',
    title: 'Templates',
    description: 'Email templates',
    icon: DocumentDuplicateIcon,
    group: 'Navigation',
    action: () => navigateTo('templates'),
    keywords: ['templates', 'design', 'builder']
  },
  {
    id: 'nav-automations',
    title: 'Automations',
    description: 'Marketing automation',
    icon: BoltIcon,
    group: 'Navigation',
    action: () => navigateTo('automations'),
    keywords: ['automations', 'workflows', 'sequences'],
    condition: () => authStore.canAccessFeature('automation_features')
  },

  // Actions
  {
    id: 'create-campaign',
    title: 'New Campaign',
    description: 'Create a new email campaign',
    icon: PlusIcon,
    group: 'Actions',
    action: () => navigateTo('campaigns.create'),
    keywords: ['new', 'create', 'campaign', 'email'],
    shortcut: '⌘ + N'
  },
  {
    id: 'create-contact',
    title: 'Add Contact',
    description: 'Add a new contact',
    icon: PlusIcon,
    group: 'Actions',
    action: () => navigateTo('contacts.create'),
    keywords: ['add', 'contact', 'subscriber']
  },
  {
    id: 'create-template',
    title: 'New Template',
    description: 'Create email template',
    icon: PlusIcon,
    group: 'Actions',
    action: () => navigateTo('templates.create'),
    keywords: ['template', 'design', 'create']
  },

  // Settings
  {
    id: 'settings',
    title: 'Settings',
    description: 'Account settings',
    icon: CogIcon,
    group: 'Settings',
    action: () => navigateTo('settings'),
    keywords: ['settings', 'preferences', 'config']
  },
  {
    id: 'profile',
    title: 'Profile',
    description: 'Edit profile',
    icon: UserIcon,
    group: 'Settings',
    action: () => navigateTo('profile'),
    keywords: ['profile', 'account', 'user']
  },
  {
    id: 'logout',
    title: 'Sign Out',
    description: 'Sign out of account',
    icon: ArrowRightOnRectangleIcon,
    group: 'Settings',
    action: () => authStore.logout(),
    keywords: ['logout', 'sign out', 'exit']
  }
]

// Filter commands based on query and conditions
const filteredCommands = computed(() => {
  let filtered = commands.filter(command => {
    // Check condition if exists
    if (command.condition && !command.condition()) {
      return false
    }
    
    // Filter by query
    if (!query.value) return true
    
    const searchTerm = query.value.toLowerCase()
    return (
      command.title.toLowerCase().includes(searchTerm) ||
      command.description?.toLowerCase().includes(searchTerm) ||
      command.keywords?.some(keyword => keyword.includes(searchTerm))
    )
  })
  
  return filtered
})

// Group commands
const groupedCommands = computed(() => {
  const groups = {}
  
  filteredCommands.value.forEach(command => {
    if (!groups[command.group]) {
      groups[command.group] = {
        name: command.group,
        commands: []
      }
    }
    groups[command.group].commands.push(command)
  })
  
  return Object.values(groups)
})

// Get flat array for navigation
const flatCommands = computed(() => filteredCommands.value)

const isSelected = (command, groupIndex) => {
  const flatIndex = flatCommands.value.findIndex(c => c.id === command.id)
  return flatIndex === selectedIndex.value
}

// Keyboard navigation
const handleKeydown = (event) => {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      selectedIndex.value = Math.min(selectedIndex.value + 1, flatCommands.value.length - 1)
      break
    case 'ArrowUp':
      event.preventDefault()
      selectedIndex.value = Math.max(selectedIndex.value - 1, 0)
      break
    case 'Enter':
      event.preventDefault()
      if (flatCommands.value[selectedIndex.value]) {
        executeCommand(flatCommands.value[selectedIndex.value])
      }
      break
    case 'Escape':
      event.preventDefault()
      handleClose()
      break
  }
}

const executeCommand = (command) => {
  command.action()
  handleClose()
}

const handleClose = () => {
  document.removeEventListener('keydown', handleGlobalKeydown)
  emit('close')
}

const handleGlobalKeydown = (event) => {
  if (event.key === 'Escape') {
    handleClose()
  }
}

// Click outside to close
const handleClickOutside = (event) => {
  if (paletteRef.value && !paletteRef.value.contains(event.target)) {
    handleClose()
  }
}

onMounted(async () => {
  await nextTick()
  searchInput.value?.focus()
  document.addEventListener('keydown', handleGlobalKeydown)
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeydown)
  document.removeEventListener('click', handleClickOutside)
})
</script>